import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs, { Dayjs } from 'dayjs';
import { Box, ClosableDrawer, Grid, PrimaryPaper } from '@atoms/layout';
import { Chip, Divider, Stack, styled, Typography } from '@mui/material';
import { Button } from '@atoms/buttons';
import { ShiftList } from '@libs/models/shifts/shift';
import { useNotification } from '@libs/snackbar';
import { ShiftsIcon } from '@atoms/icons';
import { DataGrid, EmptyListPlaceholder } from '@molecules/dataDisplay';
import { useShiftListColumns } from './shiftListColumns';
import { GridRowSelectionModel } from '@mui/x-data-grid';
import { useJobQualificationsData } from '@libs/store/talents';
import { useShiftsAssignment, usePublishDrawerStore } from '@libs/store/shifts';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { useLocationStore } from '@libs/store/locations';
import { LocationExtra } from '@libs/models/locations';
import { FilterMultiSelect, renderCheckboxOption } from '@atoms/inputs';
import { StartDateRangeFilterInputControlled } from '@molecules/filters';

type DrawerProps = {
  onSaveCall?: () => void;
};

const FallbackTimezone = TIMEZONE_FALLBACK;
export function PublishShiftsDrawer({ onSaveCall }: DrawerProps) {
  const intl = useIntl();
  const { showError } = useNotification();

  const {
    drawerOpen,
    loading: shiftLoading,
    shiftsToPublish,
    closeShiftPublishDrawer,
    loadPublishableShifts,
  } = usePublishDrawerStore();
  const [qualifications, setQualifications] = useState<string[]>([]);
  const { publishShifts } = useShiftsAssignment();
  const { current: currentLocation, loading: locationsLoading, currentLocationID } = useLocationStore();

  const [selectedShifts, setSelectedShifts] = useState<GridRowSelectionModel>([]);
  const [shiftsDateFilter, setShiftsDateFilter] = useState<DateFilterValue | null>(null);

  const localizationSubset = 'shifts.shiftsPublishDrawer';
  const open = drawerOpen;

  const loading = shiftLoading || locationsLoading;

  useEffect(() => {
    loadPublishableShifts({
      dateFilter: shiftsDateFilter?.value ?? {
        from: dayjs().startOf('day'),
        timezone: currentLocation?.timezone || FallbackTimezone,
      },
      locationId: currentLocationID,
      qualifications,
    });
  }, [loadPublishableShifts, shiftsDateFilter, currentLocation, qualifications, currentLocationID]);

  const handleClose = () => {
    closeShiftPublishDrawer();
  };

  const handleSubmit = async () => {
    try {
      await publishShifts(selectedShifts as string[]);
      onSaveCall && onSaveCall();
      handleClose();
    } catch (error) {
      if (error instanceof Error) {
        showError(error.message);
      }
    }
  };

  return (
    <ClosableDrawer
      open={open}
      loading={loading}
      onClose={handleClose}
      disableControlsDivider
      controls={
        <>
          <Divider sx={(theme) => ({ marginBottom: theme.spacing(2) })} />
          <Stack direction={'row-reverse'} spacing={2} sx={(theme) => ({ paddingBottom: theme.spacing(1) })}>
            <MenuButton variant="contained" onClick={handleSubmit} disabled={loading || !selectedShifts.length}>
              <FormattedMessage id={`${localizationSubset}.menuButtons.publish.label`} />
            </MenuButton>
            <Box flex={1} />
            <MenuButton
              onClick={handleClose}
              sx={(theme) => ({ color: theme.palette.text.secondary })}
              disabled={loading}
            >
              <FormattedMessage id={`${localizationSubset}.menuButtons.back.label`} />
            </MenuButton>
          </Stack>
        </>
      }
    >
      <Stack direction={'column'} sx={{ height: '100%' }} spacing={2} width={{ xs: '300px', sm: '600px' }}>
        {/* Header */}
        <Stack direction={'row'}>
          <Typography variant="h5">
            <FormattedMessage id={`${localizationSubset}.dialogHeading`} />
          </Typography>
          <Box flex={1} />
        </Stack>
        <ShiftsPage
          loading={shiftLoading}
          rows={shiftsToPublish}
          currentLocation={currentLocation}
          localizationSubset={localizationSubset}
          onSelectionChange={setSelectedShifts}
          onDateFilterChange={setShiftsDateFilter}
          onQualificationsFilterChange={setQualifications}
          qualificationsFilter={qualifications}
        />
      </Stack>
    </ClosableDrawer>
  );
}

type DateFilterValue = { key: string; value: { from: Dayjs; to: Dayjs; timezone: string } };
type ShiftsPageProps = {
  rows: ShiftList[];
  loading: boolean;
  localizationSubset: string;
  currentLocation: LocationExtra | null;
  onSelectionChange: (selection: GridRowSelectionModel) => void;
  onDateFilterChange: (value: DateFilterValue | null) => void;

  qualificationsFilter: string[];
  onQualificationsFilterChange: (value: string[]) => void;
};

function ShiftsPage({
  rows,
  loading,
  localizationSubset,
  onSelectionChange,
  onDateFilterChange,
  currentLocation,
  onQualificationsFilterChange,
  qualificationsFilter,
}: ShiftsPageProps) {
  const intl = useIntl();

  const [dateFilter, setDateFilter] = useState<DateFilterValue | null>(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const dateLabel = [dateFilter?.value.from?.format('MMM D, YYYY'), dateFilter?.value.to?.format('MMM D, YYYY')]
    .filter(Boolean)
    .join(' - ');
  const { data } = useJobQualificationsData();
  const qualificationsOptions = data.map((q) => ({
    label: q.abbreviation,
    value: q.id,
  }));

  useEffect(() => {
    onDateFilterChange && onDateFilterChange(dateFilter);
  }, [dateFilter]);
  useEffect(() => {
    onSelectionChange && onSelectionChange(rowSelectionModel);
  }, [rowSelectionModel]);

  const localizationSet = `${localizationSubset}.shiftsPickPage`;

  return (
    <PrimaryPaper sx={{ mt: 1, minHeight: 'calc(100% - 50px)' }}>
      <Stack direction={'column'} spacing={2} sx={{ height: '100%' }}>
        <Grid container spacing={2}>
          <Grid xs={4}>
            <StartDateRangeFilterInputControlled
              loading={loading}
              timezone={currentLocation?.timezone || FallbackTimezone}
              onClear={() => setDateFilter(null)}
              onSetFilter={(filter) => setDateFilter(filter as DateFilterValue)}
              dateLabel={dateLabel}
              dateValue={dateFilter?.value}
              label={<FormattedMessage id="shifts.publish.dateField.label" />}
            />
          </Grid>
          <Grid xs={4}>
            <FilterMultiSelect
              size="small"
              inlineLabel
              label={<FormattedMessage id="talents.qualification" />}
              fullWidth
              renderOption={renderCheckboxOption}
              options={qualificationsOptions}
              onApply={(v) => onQualificationsFilterChange(v)}
              onClear={() => onQualificationsFilterChange([])}
              value={qualificationsFilter}
            />
          </Grid>
          <Grid xs={4}>
            <Stack direction={'row'} alignContent={'end'} justifyContent={'end'} sx={{ marginTop: '4px' }}>
              <Chip
                disabled={loading}
                label={intl.formatMessage(
                  { id: `${localizationSet}.shiftsSelectionCounter` },
                  { amount: rowSelectionModel.length },
                )}
              />
            </Stack>
          </Grid>
        </Grid>
        <Stack spacing={2} sx={{
          height: '100%',
          maxHeight: 'calc(100% - 80px)',
        }}>
          <DataGrid
            rows={rows}
            loading={loading}
            rowHeight={64}
            columns={useShiftListColumns()}
            autoPageSize
            hideFooter
            sx={{ minHeight: '300px' }}
            disableVirtualization={false}
            checkboxSelection
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            noRowsOverlay={
              <EmptyListPlaceholder
                icon={<ShiftsIcon active />}
                title={<FormattedMessage id="shifts.table.noRows.title" />}
                subtitle={<FormattedMessage id="shifts.table.noRows.subtitle" />}
              />
            }
          />
        </Stack>
      </Stack>
    </PrimaryPaper>
  );
}

const MenuButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(16.75),
}));
